import type { ApiLead } from '@/api/conversations/types/index.ts';
import type { Lead } from '@/entities/types/LeadType.ts';
import ConversationLeadPhone from '@/entities/ConversationLeadPhone.ts';
import Model from '@/entities/Model.js';
import ActivixDate from '@/value-objects/ActivixDate.js';

export type { ApiLead } from '@/api/conversations/types/index.ts';

export default class ConversationLead extends Model {
    declare id: number;

    declare accountId: number;

    declare customerId: number | null;

    declare fullName: string;

    declare divisionId: number | null;

    declare cellPhones: ConversationLeadPhone[];

    declare isUnsubscribedToSms: boolean;

    declare isCaslExpired: boolean;

    declare locale: string;

    static get defaults() {
        return {
            id: 0,
            accountId: 0,
            customerId: null,
            fullName: '',
            divisionId: null,
            cellPhones: [],
            isUnsubscribedToSms: false,
            isCaslExpired: false,
            locale: 'en',
        };
    }

    static fromApi(lead: ApiLead): ConversationLead {
        return new ConversationLead({
            id: lead.id,
            accountId: lead.account_id,
            customerId: lead.customer_id,
            fullName: `${lead.first_name} ${lead.last_name}`.trim(),
            divisionId: lead.division_id,
            cellPhones: [],
            isUnsubscribedToSms:
                !!lead.unsubscribe_sms_date ||
                (!!lead.unsubscribe_all_date &&
                    new ActivixDate(lead.unsubscribe_all_date).isFuture()),
            isCaslExpired: lead.is_casl_expired,
            locale: lead.locale ?? 'en',
        });
    }

    static fromLead(lead: Lead): ConversationLead {
        return new ConversationLead({
            id: lead.id,
            accountId: lead.account_id,
            customerId: lead.customer_id,
            fullName: lead.fullName,
            divisionId: lead.division_id,
            cellPhones: lead.getMobilePhones().map(ConversationLeadPhone.fromApi),
            isUnsubscribedToSms: lead.isUnsubscribedToSms(),
            isCaslExpired: lead.isCaslExpired(),
            locale: lead.locale ?? 'en',
        });
    }

    constructor(initialValues: Partial<ConversationLead> = {}) {
        super(initialValues);
    }
}
